import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link as RouterLink, useLocation } from 'react-router-dom';
import { Link as ScrollLink, animateScroll as scroll } from 'react-scroll';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Phone, Mail, Shield, Car, AlertTriangle, check, 
  ChevronDown, ArrowRight, Star, Menu, X, CheckCircle 
} from 'lucide-react';

// --- Assets & Constants ---
const TFN = "18882770497";
const PHONE_DISPLAY = "+1 (888)-277-0497";
const EMAIL = "techatronsolutionsllc@gmail.com";
const ADDRESS = "4315 Turnberry Dr Fredericksburg, VA 22408";

// Animation Variants
const fadeInUp = {
  hidden: { opacity: 0, y: 60 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2
    }
  }
};

// --- Components ---

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: "About", to: "about" },
    { name: "Services", to: "services" },
    { name: "FAQ", to: "faq" },
    { name: "Contact", to: "contact" },
  ];

  return (
    <nav className={`fixed w-full z-50 transition-all duration-300 ${isScrolled ? 'bg-brand-dark/95 backdrop-blur-md shadow-lg py-2' : 'bg-transparent py-4'}`}>
      <div className="container mx-auto px-6 flex justify-between items-center">
        <div className="text-2xl font-bold text-white tracking-tighter cursor-pointer" onClick={() => window.location.href='/'}>
          Auto<span className="text-brand-accent">Premium</span>
        </div>

        {/* Desktop Nav */}
        <div className="hidden md:flex items-center space-x-8">
          {isHome && navLinks.map((link) => (
            <ScrollLink 
              key={link.name} 
              to={link.to} 
              smooth={true} 
              duration={800} 
              className="text-gray-300 hover:text-brand-cta cursor-pointer transition-colors font-medium"
            >
              {link.name}
            </ScrollLink>
          ))}
           <a href={`tel:${TFN}`} className="bg-brand-cta hover:bg-orange-600 text-white px-6 py-2 rounded-full font-bold shadow-lg transform hover:scale-105 transition-all flex items-center gap-2">
            <Phone size={18} /> Call Now
          </a>
        </div>

        {/* Mobile Nav Toggle */}
        <div className="md:hidden text-white" onClick={() => setIsOpen(!isOpen)}>
          {isOpen ? <X /> : <Menu />}
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div 
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-brand-dark border-t border-gray-700"
          >
            <div className="flex flex-col p-6 space-y-4">
              {isHome ? navLinks.map((link) => (
                <ScrollLink 
                  key={link.name} 
                  to={link.to} 
                  smooth={true} 
                  onClick={() => setIsOpen(false)}
                  className="text-gray-300 text-lg font-medium"
                >
                  {link.name}
                </ScrollLink>
              )) : <RouterLink to="/" className="text-gray-300">Back Home</RouterLink>}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

const Hero = () => {
  return (
    <section className="relative h-screen w-full flex items-center justify-center overflow-hidden bg-brand-dark">
      {/* Background with Gradient Overlay */}
      <div className="absolute inset-0 z-0">
        <img 
          src="https://images.unsplash.com/photo-1492144534655-ae79c964c9d7?ixlib=rb-1.2.1&auto=format&fit=crop&w=1950&q=80" 
          alt="Luxury Car" 
          className="w-full h-full object-cover opacity-30"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-brand-dark via-brand-dark/80 to-transparent" />
      </div>

      <div className="container mx-auto px-6 relative z-10 grid md:grid-cols-2 gap-12 items-center">
        <motion.div 
          initial="hidden"
          animate="visible"
          variants={staggerContainer}
          className="text-white"
        >
          <motion.div variants={fadeInUp} className="flex items-center gap-2 mb-4">
            <span className="px-3 py-1 bg-brand-accent/20 text-brand-accent rounded-full text-sm font-semibold border border-brand-accent/50">
              Trusted by 5,000+ Drivers
            </span>
          </motion.div>
          
          <motion.h1 variants={fadeInUp} className="text-5xl md:text-7xl font-extrabold leading-tight mb-6">
            Protection That <br/>
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-accent to-blue-600">
              Moves With You.
            </span>
          </motion.h1>
          
          <motion.p variants={fadeInUp} className="text-gray-300 text-lg mb-8 max-w-lg">
            Stop overpaying for uncertainty. We connect you with top-tier carriers to secure the coverage you actually need. 
          </motion.p>

          <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row gap-4">
            {/* Primary CTA: Phone Number */}
            <a 
              href={`tel:${TFN}`} 
              className="group bg-brand-cta hover:bg-orange-600 text-white px-8 py-4 rounded-xl font-bold text-xl shadow-[0_0_20px_rgba(249,115,22,0.5)] transform hover:scale-105 transition-all flex items-center justify-center gap-3"
            >
              <Phone className="animate-pulse" />
              <span>{PHONE_DISPLAY}</span>
            </a>
            
            <ScrollLink 
              to="contact" 
              smooth={true}
              className="cursor-pointer px-8 py-4 rounded-xl font-bold text-white border border-white/20 hover:bg-white/10 backdrop-blur-sm transition-all flex items-center justify-center"
            >
              Get a Quote
            </ScrollLink>
          </motion.div>
        </motion.div>

        {/* 3D Visual Element */}
        <motion.div 
          initial={{ opacity: 0, x: 100 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 1, delay: 0.5 }}
          className="hidden md:block relative perspective-1000"
        >
           {/* Abstract 3D Glass Card Effect */}
           <div className="relative w-full h-[400px] bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl rounded-2xl border border-white/10 shadow-2xl p-8 transform rotate-y-12 rotate-x-6 hover:rotate-0 transition-transform duration-700">
              <div className="absolute top-4 right-4 text-brand-accent">
                <Shield size={48} />
              </div>
              <div className="mt-20">
                <h3 className="text-3xl text-white font-bold mb-2">Full Coverage</h3>
                <p className="text-gray-400">Liability • Collision • Comprehensive</p>
                <div className="mt-8 flex items-center gap-4">
                  <div className="h-2 w-full bg-gray-700 rounded-full overflow-hidden">
                    <div className="h-full bg-brand-cta w-[85%]"></div>
                  </div>
                  <span className="text-white font-mono">85% Saved</span>
                </div>
              </div>
           </div>
        </motion.div>
      </div>

      <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 animate-bounce text-white/50">
        <ChevronDown size={32} />
      </div>
    </section>
  );
};

const About = () => {
  return (
    <section id="about" className="py-20 bg-brand-light">
      <div className="container mx-auto px-6">
        <div className="grid md:grid-cols-2 gap-16 items-center">
          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <h2 className="text-brand-cta font-bold uppercase tracking-widest mb-2">Why Choose Us</h2>
            <h3 className="text-4xl font-bold text-brand-dark mb-6">Navigating Insurance Shouldn't Be a Car Crash.</h3>
            <p className="text-gray-600 text-lg mb-6 leading-relaxed">
              Most drivers don't know if they are under-insured until it's too late. Or worse, they are paying for coverage they don't need. 
            </p>
            <p className="text-gray-600 text-lg mb-8 leading-relaxed">
              At <span className="font-bold text-brand-dark">Techatron Solutions</span>, we strip away the jargon. As licensed agents, we compare policies from giants to find your perfect fit. We don't just sell plans; we solve problems.
            </p>
            
            <ul className="space-y-4 mb-8">
              {["Licensed Experts", "Instant Claims Support", "Competitive Rates"].map((item, i) => (
                <li key={i} className="flex items-center gap-3 text-brand-dark font-medium">
                  <CheckCircle className="text-brand-cta" size={20} /> {item}
                </li>
              ))}
            </ul>

            <ScrollLink to="contact" smooth={true} className="inline-flex items-center gap-2 text-brand-cta font-bold hover:gap-4 transition-all cursor-pointer">
              Speak to an Agent <ArrowRight size={20} />
            </ScrollLink>
          </motion.div>

          <div className="relative">
            <img 
              src="https://images.unsplash.com/photo-1573164713714-d95e436ab8d6?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80" 
              alt="Agent helping client" 
              className="rounded-2xl shadow-2xl z-10 relative"
            />
            <div className="absolute -bottom-6 -left-6 bg-white p-6 rounded-xl shadow-xl z-20 hidden md:block">
              <div className="flex items-center gap-2 mb-2">
                <Star className="text-yellow-400 fill-current" />
                <Star className="text-yellow-400 fill-current" />
                <Star className="text-yellow-400 fill-current" />
                <Star className="text-yellow-400 fill-current" />
                <Star className="text-yellow-400 fill-current" />
              </div>
              <p className="font-bold text-brand-dark">"Saved me $400/year!"</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const Services = () => {
  const services = [
    { title: "Liability Insurance", icon: <Shield />, desc: "Essential coverage for damages to others." },
    { title: "Collision Coverage", icon: <Car />, desc: "Repair costs for your vehicle after an accident." },
    { title: "Comprehensive", icon: <CheckCircle />, desc: "Protection against theft, weather, and vandalism." },
    { title: "Personal Injury (PIP)", icon: <AlertTriangle />, desc: "Medical expenses for you and your passengers." },
    { title: "Uninsured Motorist", icon: <Shield />, desc: "Protection when the other driver can't pay." },
    { title: "Gap Insurance", icon: <Car />, desc: "Covers the loan vs. value difference if totaled." },
  ];

  return (
    <section id="services" className="py-20 bg-brand-dark text-white relative overflow-hidden">
      {/* Abstract bg shapes */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-brand-accent/10 rounded-full blur-3xl" />
      
      <div className="container mx-auto px-6 relative z-10">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold mb-4">Comprehensive Protection</h2>
          <p className="text-gray-400 max-w-2xl mx-auto">From fender benders to total loss, we have a policy that fits your specific needs.</p>
        </div>

        <motion.div 
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid md:grid-cols-3 gap-8"
        >
          {services.map((service, index) => (
            <motion.div 
              key={index}
              variants={fadeInUp}
              className="group bg-white/5 backdrop-blur-sm border border-white/10 p-8 rounded-2xl hover:bg-white/10 transition-all duration-300 transform hover:-translate-y-2"
            >
              <div className="w-14 h-14 bg-brand-cta/20 text-brand-cta rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                {service.icon}
              </div>
              <h3 className="text-xl font-bold mb-3">{service.title}</h3>
              <p className="text-gray-400 text-sm leading-relaxed mb-6">{service.desc}</p>
              
              <ScrollLink to="contact" smooth={true} className="text-brand-accent text-sm font-bold flex items-center gap-2 cursor-pointer group-hover:gap-3 transition-all">
                Get Covered <ArrowRight size={16} />
              </ScrollLink>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

const Contact = () => {
  return (
    <section id="contact" className="py-20 bg-white">
      <div className="container mx-auto px-6">
        <div className="bg-brand-dark rounded-3xl overflow-hidden shadow-2xl flex flex-col md:flex-row">
          <div className="p-12 md:w-1/2 flex flex-col justify-center">
            <h2 className="text-4xl font-bold text-white mb-6">Let's Secure Your Ride Today.</h2>
            <p className="text-gray-300 mb-8">
              Questions about your policy? Need a quote? Our agents are ready to assist you immediately.
            </p>
            
            <div className="space-y-6">
              <a 
                href={`tel:${TFN}`} 
                className="flex items-center gap-4 bg-brand-cta hover:bg-orange-600 text-white p-4 rounded-xl transition-all shadow-lg group"
              >
                <div className="bg-white/20 p-3 rounded-full group-hover:rotate-12 transition-transform">
                  <Phone size={24} />
                </div>
                <div>
                  <div className="text-xs text-orange-200 uppercase font-bold tracking-wider">Call Us Now</div>
                  <div className="text-2xl font-bold">{PHONE_DISPLAY}</div>
                </div>
              </a>

              <a 
                href={`mailto:${EMAIL}`} 
                className="flex items-center gap-4 bg-gray-800 hover:bg-gray-700 text-white p-4 rounded-xl transition-all border border-gray-700 group"
              >
                <div className="bg-white/10 p-3 rounded-full group-hover:rotate-12 transition-transform">
                  <Mail size={24} />
                </div>
                <div>
                  <div className="text-xs text-gray-400 uppercase font-bold tracking-wider">Email Us</div>
                  <div className="text-xl font-bold">{EMAIL}</div>
                </div>
              </a>
            </div>
            
            <div className="mt-8 text-gray-400 text-sm">
              <p>{ADDRESS}</p>
            </div>
          </div>
          
          <div className="md:w-1/2 bg-brand-accent relative overflow-hidden h-64 md:h-auto">
             <img 
               src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80" 
               alt="Happy Customer" 
               className="w-full h-full object-cover mix-blend-multiply opacity-80"
             />
             <div className="absolute inset-0 bg-gradient-to-l from-transparent to-brand-dark/90 md:bg-gradient-to-r md:from-brand-dark md:to-transparent" />
          </div>
        </div>
      </div>
    </section>
  );
};

const FAQ = () => {
  const faqs = [
    { q: "What does vehicle insurance cover?", a: "Vehicle insurance typically covers damages to your car, third-party liabilities, medical expenses, and theft. Coverage options vary, including comprehensive insurance (covering own damage and third-party claims) and third-party insurance (covering damages to others but not your vehicle)." },
    { q: "Can I transfer my vehicle insurance to a new owner?", a: "Yes, vehicle insurance can be transferred when selling a car. The new owner must apply for the policy transfer within 14 days of purchase, providing necessary documents like the RC (Registration Certificate) and sale agreement." },
    { q: "How is my vehicle insurance premium calculated?", a: "Your premium is based on factors like the type of vehicle, age, location, driving history, coverage type, and add-ons. Insurers also consider your No Claim Bonus (NCB) if you have a history of claim-free years." },
    { q: "What should I do after an accident to claim insurance?", a: "In case of an accident, inform your insurer immediately, file an FIR if required, take pictures of the damage, and submit the necessary claim forms and documents. The insurer will assess the damage and process the claim accordingly." }
  ];

  return (
    <section id="faq" className="py-20 bg-brand-light">
      <div className="container mx-auto px-6 max-w-4xl">
        <h2 className="text-3xl font-bold text-center text-brand-dark mb-12">Frequently Asked Questions</h2>
        <div className="space-y-4">
          {faqs.map((item, i) => (
            <FAQItem key={i} question={item.q} answer={item.a} />
          ))}
        </div>
        
        <div className="text-center mt-12">
          <p className="text-gray-600 mb-4">Still have questions?</p>
          <ScrollLink to="contact" smooth={true} className="text-brand-cta font-bold hover:underline cursor-pointer">
            Contact Support
          </ScrollLink>
        </div>
      </div>
    </section>
  );
};

const FAQItem = ({ question, answer }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      <button 
        onClick={() => setIsOpen(!isOpen)}
        className="w-full text-left p-6 flex justify-between items-center focus:outline-none"
      >
        <span className="font-bold text-gray-800">{question}</span>
        <ChevronDown className={`transform transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div 
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="px-6 pb-6 text-gray-600"
          >
            {answer}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const Footer = () => {
  return (
    <footer className="bg-gray-900 text-gray-400 py-12 text-sm border-t border-gray-800">
      <div className="container mx-auto px-6">
        <div className="flex flex-col md:flex-row justify-between items-center mb-8">
            <div className="text-2xl font-bold text-white mb-4 md:mb-0">Auto<span className="text-brand-cta">Premium</span></div>
            <div className="flex gap-6">
              <RouterLink to="/privacy-policy" className="hover:text-white transition-colors">Privacy Policy</RouterLink>
            </div>
        </div>
        
        <div className="border-t border-gray-800 py-8 mb-4">
          <h4 className="text-white font-bold mb-2">Disclaimer</h4>
          <p className="leading-relaxed text-xs">
            We are Authorized Insurance Agents of the Services Providers/ Home and Auto Insurance services to assist the drivers and home owners in connecting with local service providers. All the contractors/ providers are independent and auto insurance and home insurance does not warrant or guarantee any work performed. It is the responsibility of the Drivers and Home Owners to verify that the hired contractor furnishes the necessary licence and the insurance required for the work being performed. All persons depicted in the photos or videos are actors or models and not contractors listed on Auto insurance and home Insurance services. Many or most of the images used in the site are either Stock Free or AI generated.
          </p>
        </div>
        
        <div className="text-center">
          © {new Date().getFullYear()} Techatron Solutions LLC. All rights reserved. <br/>
          {ADDRESS}
        </div>
      </div>
    </footer>
  );
};

// --- Privacy Policy Page ---
const PrivacyPolicy = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="bg-brand-light min-h-screen">
      <nav className="bg-brand-dark py-4 text-white">
        <div className="container mx-auto px-6">
            <RouterLink to="/" className="flex items-center gap-2 font-bold hover:text-brand-accent">
                <ArrowRight className="transform rotate-180" /> Back to Home
            </RouterLink>
        </div>
      </nav>
      <div className="container mx-auto px-6 py-12 max-w-3xl">
        <h1 className="text-4xl font-bold text-brand-dark mb-8">Privacy Policy</h1>
        
        <div className="bg-white p-8 rounded-2xl shadow-lg space-y-6 text-gray-700">
            <p className="text-lg font-bold">Effective Date: {new Date().toLocaleDateString()}</p>
            
            <section>
                <h2 className="text-2xl font-bold text-brand-dark mb-3">1. No Data Collection</h2>
                <p>
                    At Auto Premium Insurance (Techatron Solutions LLC), we prioritize your privacy above all else. 
                    We want to be explicitly clear: <strong>This website does not collect, store, or process any personal data from its visitors.</strong>
                </p>
            </section>

            <section>
                <h2 className="text-2xl font-bold text-brand-dark mb-3">2. No Cookies or Tracking</h2>
                <p>
                    We do not use cookies, pixels, or any tracking technologies. When you visit our landing page, 
                    no information about your device, location, or browsing behavior is recorded by our servers. 
                    There are no session identifiers or local storage data used.
                </p>
            </section>

            <section>
                <h2 className="text-2xl font-bold text-brand-dark mb-3">3. Communication</h2>
                <p>
                    The only way we interact with you is if you voluntarily choose to click our "Call Us" or "Email Us" buttons. 
                    These actions are handled entirely by your own device's default dialer or email client. We do not store your phone number or email address in any database associated with this website.
                </p>
            </section>

            <section>
                <h2 className="text-2xl font-bold text-brand-dark mb-3">4. Third-Party Links</h2>
                <p>
                    This site is designed purely for informational purposes to connect you with our agents. We do not sell data to third parties because we do not collect data to begin with.
                </p>
            </section>
            
            <section className="pt-6 border-t border-gray-100">
                <p className="text-sm">
                    If you have any questions regarding this strict privacy standard, please contact us directly at <span className="font-bold">{EMAIL}</span>.
                </p>
            </section>
        </div>
      </div>
      <Footer />
    </div>
  );
};

// --- Main App Wrapper ---
function App() {
  return (
    <Router>
      <Routes>
        <Route path="/" element={
          <div className="font-sans antialiased text-gray-900 bg-white selection:bg-brand-cta selection:text-white">
            <Navbar />
            <Hero />
            <About />
            <Services />
            <Contact />
            <FAQ />
            <Footer />
          </div>
        } />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      </Routes>
    </Router>
  );
}

export default App;